import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LinearProgress, Box, Typography, Button } from '@mui/material'
import { useFarmId } from '../common/use-farm-id'
import { setLastFarmId, useOrgContextDispatch } from '../common/context/organization/org-context'
import Dashboard from './dashboard'
import { AddFarmModal } from '../farm/add-farm/add-farm-modal'
import { useCurrentUser } from '~/common/use-current-user'
import { useUserFarms } from '../common/use-user-farms'
import { useSeasonAutoDefault } from '~/farm/use-season-auto-default'

/**
 * Dashboard route handler
 * Renders unified Dashboard component which handles farm and plot views internally
 * Uses useFarmId hook for smart farmId resolution (URL → Context → Default)
 * Saves farmId to context when it comes from URL (for superadmins)
 */
function DashboardRouter() {
  const navigate = useNavigate()
  const dispatch = useOrgContextDispatch()
  const { farmId, loading, source } = useFarmId()
  useSeasonAutoDefault()
  const { currentUser } = useCurrentUser()
  const { userFarms, loading: farmsLoading } = useUserFarms({ user: currentUser })
  const [showFarmModal, setShowFarmModal] = useState(false)

  useEffect(() => {
    function checkFarms() {
      if (!farmsLoading && userFarms !== undefined && userFarms !== null && userFarms.length === 0) {
        setShowFarmModal(true)
      }
    }
    checkFarms()
  }, [farmsLoading, userFarms])

  useEffect(() => {
    // Use window.location.pathname — React Router's useLocation lags one render
    // behind the actual URL during route transitions, causing stale effect fires.
    const pathname = window.location.pathname

    // Guard: only update URL when we are actually on /dashboard.
    if (pathname !== '/dashboard') return

    // If farmId came from context or default, update URL
    if (farmId && (source === 'context' || source === 'default')) {
      navigate(`/dashboard?farmId=${farmId}`, { replace: true })
      return
    }

    // If farmId came from URL, save to context for superadmins
    if (farmId && source === 'url') {
      dispatch(setLastFarmId({ farmId }))
    }
  }, [farmId, source, navigate, dispatch])

  // If we have farmId, show unified dashboard, wrapped in the point provider
  if (farmId) {
    return <Dashboard />
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    )
  }

  // No farm available - show message
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pt: '10rem',
        gap: '5rem',
      }}
    >
      {/* If there are multiple farm under the user, prompt the dropdown msg */}
      {(currentUser?.permissions === 1 || userFarms?.length > 1) && (
        <>
          <Typography variant="h6" color="text.secondary">
            Please select a farm from the farm selector in the navigation bar
          </Typography>
          <Typography variant="h6" color="text.secondary">
            - or -
          </Typography>
        </>
      )}
      
      <Button onClick={() => setShowFarmModal(true)}>
        Create a Farm
      </Button>
      <AddFarmModal open={showFarmModal} onClose={() => setShowFarmModal(false)} />
    </Box>
  )
}

export default DashboardRouter
